import React from 'react';

import { useIntl } from 'react-intl';

import { INutritionInfo } from '@rbi-ctg/menu';
import { INutritionTableFragment } from 'generated/sanity-graphql';

import { Table, TableHead, TableRow, TableTitle } from '../styled/table';

import { NutritionItem } from './nutrition-item';
import { NutritionItemGroup } from './nutrition-item-group';
import { SanityNutritionItem, SanityNutritionItemGroup } from './types';

type NutritionTableProps = {
  nutritionTables: readonly INutritionTableFragment[];
  nutritionInfo: INutritionInfo;
  roundNutritionValue: (val: number) => number;
};

interface NutritionTableData {
  titles: string[];
  nutritionItems: { items: SanityNutritionItem[] }[];
  NutritionItemGroups: { items: SanityNutritionItemGroup[] }[];
}

export const NutritionTable: React.FC<NutritionTableProps> = ({
  nutritionInfo,
  nutritionTables,
  roundNutritionValue,
}) => {
  const { formatMessage } = useIntl();
  const nutritionLabel = formatMessage({ id: 'Nutrition' });
  const nutritionTableData: NutritionTableData = {
    titles: [nutritionLabel],
    nutritionItems: [] as { items: SanityNutritionItem[] }[],
    NutritionItemGroups: [],
  };

  nutritionTables?.forEach((nutritionTable, idx) => {
    const title = nutritionTable?.title?.locale ?? '';

    // Adding titles for only first two items as we have only two type of nutrition
    if (idx < 2) {
      nutritionTableData.titles.push(title);
    }

    const nutritionItems: SanityNutritionItem[] = [];
    const NutritionItemGroups: SanityNutritionItemGroup[] = [];

    nutritionTable?.items?.forEach(
      (nutritionItem: SanityNutritionItem | SanityNutritionItemGroup) => {
        if (nutritionItem.__typename === 'NutritionItem') {
          nutritionItems.push(nutritionItem);
        } else if (nutritionItem.__typename === 'NutritionItemGroup') {
          NutritionItemGroups.push(nutritionItem);
        }
      }
    );

    if (nutritionItems?.length > 0) {
      nutritionTableData.nutritionItems.push({
        items: nutritionItems,
      });
    }

    if (NutritionItemGroups?.length > 0) {
      nutritionTableData.NutritionItemGroups.push({
        items: NutritionItemGroups,
      });
    }
  });

  return (
    <>
      <Table data-testid="nutrition-table">
        <TableHead>
          <TableRow>
            {nutritionTableData?.titles?.map((title, index) => (
              <TableTitle key={`${title}-${index}`}>{title}</TableTitle>
            ))}
          </TableRow>
        </TableHead>
        <NutritionItem
          nutritionInfo={nutritionInfo}
          nutritionItems={nutritionTableData?.nutritionItems}
          roundNutritionValue={roundNutritionValue}
        />
      </Table>
      {nutritionTableData?.NutritionItemGroups?.length > 0 && (
        <NutritionItemGroup
          nutritionInfo={nutritionInfo}
          nutritionItemGroups={nutritionTableData?.NutritionItemGroups}
          roundNutritionValue={roundNutritionValue}
        />
      )}
    </>
  );
};
